<template>
  <div class="user-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>账号管理</span>
          <div class="header-actions">
            <el-button type="primary" @click="handleCreate">新增用户</el-button>
            <el-button @click="goOrgDept">组织架构</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="关键词">
          <el-input
            v-model="queryParams.username"
            placeholder="用户名/手机号/真实姓名"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="用户角色">
          <el-select v-model="queryParams.roleCode" placeholder="全部" clearable filterable style="width: 200px">
            <el-option
              v-for="role in roleList"
              :key="role.id"
              :label="role.roleName"
              :value="role.roleCode"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="部门ID">
          <el-input-number v-model="queryParams.deptId" :min="1" :step="1" placeholder="可选" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 用户列表 -->
      <el-table
        v-loading="loading"
        :data="userList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="username" label="用户名" width="120" />
        <el-table-column prop="realName" label="真实姓名" width="120" />
        <el-table-column prop="phone" label="手机号" width="120" />
        <el-table-column prop="email" label="邮箱" width="180" />
        <el-table-column prop="roleName" label="用户角色" width="150">
          <template #default="{ row }">
            {{ row.roleName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="lastLoginTime" label="最后登录时间" width="180">
          <template #default="{ row }">
            {{ row.lastLoginTime ? formatDateTime(row.lastLoginTime) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" fixed="right">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
              <el-button type="warning" size="small" @click="handleResetPwd(row)">重置密码</el-button>
              <el-dropdown trigger="click" @command="handleDropdownCommand">
                <el-button type="info" size="small">
                  更多<el-icon class="el-icon--right"><arrow-down /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item 
                      :command="{action: 'toggleStatus', row: row}"
                    >
                      <el-icon><Switch /></el-icon>
                      {{ row.status === 1 ? '禁用' : '启用' }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.pageNum"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑用户对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="userFormRef"
        :model="userForm"
        :rules="userFormRules"
        label-width="100px"
        :validate-on-rule-change="false"
      >
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" :disabled="isEdit" />
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEdit">
          <el-input v-model="userForm.password" type="password" show-password />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="userForm.phone" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" />
        </el-form-item>
        <el-form-item label="用户角色" prop="roleCode">
          <el-select 
            v-model="userForm.roleCode" 
            :disabled="isEdit && userForm.lastLoginTime" 
            placeholder="请选择角色"
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="role in roleList"
              :key="role.id"
              :label="role.roleName"
              :value="role.roleCode"
            />
          </el-select>
          <div v-if="isEdit && userForm.lastLoginTime" style="color: #f56c6c; font-size: 12px; margin-top: 4px;">
            该用户已在使用中，无法修改用户角色
          </div>
        </el-form-item>
        <el-form-item label="真实姓名" prop="realName" required>
          <el-input v-model="userForm.realName" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="userForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog v-model="resetPwdDialogVisible" title="重置密码" width="400px">
      <el-form ref="resetPwdFormRef" :model="resetPwdForm" :rules="resetPwdRules" label-width="100px">
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="resetPwdForm.newPassword" type="password" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="resetPwdDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleResetPwdSubmit">确定</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown, Switch } from '@element-plus/icons-vue'
import {
  getUserList,
  getUserById,
  createUser,
  updateUser,
  updateUserStatus,
  resetPassword,
  getCurrentCompany
} from '../api/managerUser'
import { getRoleOptions } from '../api/sysRole'

const router = useRouter()

// 数据
const loading = ref(false)
const userList = ref([])
const total = ref(0)
const currentCompany = ref(null)
const roleList = ref([])
const queryParams = reactive({
  username: '',
  phone: '',
  roleCode: '',
  status: null,
  deptId: null,
  pageNum: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增用户')
const isEdit = ref(false)
const userFormRef = ref(null)
const userForm = reactive({
  id: null,
  username: '',
  password: '',
  phone: '',
  email: '',
  roleCode: '',
  realName: '',
  companyId: null,
  status: 1,
  lastLoginTime: null,
  roleId: null
})

const userFormRules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
  phone: [
    {
      validator: (rule, value, callback) => {
        if (!value) return callback()
        const ok = /^1[3-9]\d{9}$/.test(value)
        if (!ok) return callback(new Error('手机号格式不正确'))
        return callback()
      },
      trigger: 'blur'
    }
  ],
  roleCode: [{ required: true, message: '请选择用户角色', trigger: 'blur' }],
  realName: [{ required: true, message: '请输入真实姓名', trigger: 'blur' }]
}

// 重置密码对话框
const resetPwdDialogVisible = ref(false)
const resetPwdFormRef = ref(null)
const resetPwdForm = reactive({
  userId: null,
  newPassword: ''
})

const resetPwdRules = {
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ]
}

// 方法
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN')
}

const loadUserList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams }
    const res = await getUserList(params)
    if (res.code === '000000') {
      userList.value = res.data.records || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取用户列表失败:', error)
    ElMessage.error('获取用户列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.pageNum = 1
  loadUserList()
}

const handleReset = () => {
  queryParams.username = ''
  queryParams.phone = ''
  queryParams.roleCode = ''
  queryParams.status = null
  queryParams.deptId = null
  queryParams.pageNum = 1
  loadUserList()
}

const goOrgDept = () => {
  router.push('/home/manager-org-dept')
}

const handleCreate = () => {
  dialogTitle.value = '新增用户'
  isEdit.value = false
  Object.assign(userForm, {
    id: null,
    username: '',
    password: '',
    phone: '',
    email: '',
    roleCode: '',
    realName: '',
    companyId: currentCompany.value?.id || null,
    status: 1,
    lastLoginTime: null,
    roleId: null
  })
  dialogVisible.value = true
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑用户'
  isEdit.value = true
  try {
    const res = await getUserById(row.id)
    if (res.code === '000000') {
      Object.assign(userForm, {
        id: res.data.id,
        username: res.data.username,
        phone: res.data.phone || '',
        email: res.data.email || '',
        roleCode: res.data.roleCode || '',
        realName: res.data.realName || '',
        companyId: res.data.companyId,
        status: res.data.status,
        lastLoginTime: res.data.lastLoginTime,
        roleId: res.data.roleId || null
      })
      dialogVisible.value = true
    }
  } catch (error) {
    console.error('获取用户详情失败:', error)
    ElMessage.error('获取用户详情失败')
  }
}

const handleSubmit = async () => {
  if (!userFormRef.value) return
  await userFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const data = { ...userForm }
        if (!isEdit.value && !data.password) {
          ElMessage.warning('请输入密码')
          return
        }
        // 确保企业ID为当前管理员的企业
        data.companyId = currentCompany.value?.id || null
        // phone 为空字符串时转为 null（支持多个无手机号的业务员）
        if (!data.phone || data.phone.trim() === '') {
          data.phone = null
        }
        const res = isEdit.value ? await updateUser(data) : await createUser(data)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadUserList()
        }
      } catch (error) {
        console.error('保存用户失败:', error)
        const errorMessage = error.response?.data?.message || error.message || '保存用户失败'
        const isRoleError = errorMessage.includes('无法修改用户类型') || errorMessage.includes('无法修改用户角色')
        const isNetworkError = !error.response && error.message && error.message.includes('Network Error')

        if (isRoleError) {
          // 角色限制类错误单独提示
          ElMessage.error('该用户已在使用中，无法修改用户角色')
        } else if (isNetworkError) {
          // 纯网络错误拦截器未提示，这里兜底
          ElMessage.error('网络异常，请稍后重试')
        }
        // 其余业务错误已由 axios 拦截器弹出一次，这里不再重复提示
      }
    }
  })
}

const handleDialogClose = () => {
  userFormRef.value?.resetFields()
}

const handleResetPwd = (row) => {
  resetPwdForm.userId = row.id
  resetPwdForm.newPassword = ''
  resetPwdDialogVisible.value = true
}

const handleResetPwdSubmit = async () => {
  if (!resetPwdFormRef.value) return
  await resetPwdFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = await resetPassword(resetPwdForm.userId, resetPwdForm.newPassword)
        if (res.code === '000000') {
          ElMessage.success('密码重置成功')
          resetPwdDialogVisible.value = false
        }
      } catch (error) {
        console.error('重置密码失败:', error)
        ElMessage.error(error.response?.data?.message || '重置密码失败')
      }
    }
  })
}

const handleToggleStatus = async (row) => {
  const newStatus = row.status === 1 ? 0 : 1
  const action = newStatus === 1 ? '启用' : '禁用'
  try {
    await ElMessageBox.confirm(`确定要${action}该用户吗？`, '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await updateUserStatus(row.id, newStatus)
    if (res.code === '000000') {
      ElMessage.success(`${action}成功`)
      loadUserList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error(`${action}用户失败:`, error)
      ElMessage.error(error.response?.data?.message || `${action}用户失败`)
    }
  }
}

// 处理下拉菜单命令
const handleDropdownCommand = (command) => {
  const { action, row } = command
  switch (action) {
    case 'toggleStatus':
      handleToggleStatus(row)
      break
  }
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadUserList()
}

const handlePageChange = (page) => {
  queryParams.pageNum = page
  loadUserList()
}

// 加载当前管理员所属企业信息
const loadCurrentCompany = async () => {
  try {
    const res = await getCurrentCompany()
    if (res.code === '000000' && res.data) {
      currentCompany.value = res.data
    }
  } catch (error) {
    console.error('获取企业信息失败:', error)
    ElMessage.error('获取企业信息失败')
  }
}

// 加载角色列表
const loadRoleList = async () => {
  try {
    const res = await getRoleOptions()
    if (res.code === '000000' && res.data) {
      roleList.value = res.data
    }
  } catch (error) {
    console.error('获取角色列表失败:', error)
    ElMessage.error('获取角色列表失败')
  }
}

// 生命周期
onMounted(() => {
  loadCurrentCompany()
  loadUserList()
  loadRoleList()
})
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
}

.action-buttons .el-button {
  margin: 0;
}

/* 下拉菜单危险项样式 */
.danger-item {
  color: #f56c6c;
}

.danger-item:hover {
  color: #f56c6c;
  background-color: #fef0f0;
}
</style>

